import { ProductionService } from "services/production-service";
import { I18N } from "aurelia-i18n";
import { autoinject } from "aurelia-framework";
import queryStringHelper from "helpers/queryStringHelper";
import projectService from "services/projectService";
import routerHelper from "helpers/routerHelper";
import { ProductionModel } from "api/models/company/project/dispatch/production-model";

import { NotificationHelper } from "helpers/notification-helper";
import { Router } from "aurelia-router";
import { FormBase } from "pages/form-base";

@autoinject
export class DailyEntryProductionEdit extends FormBase {

    public dispatchProjectCode: string = "";
    public dispatchDate: Date = new Date();
    public line: any;
    public productionItemId: number = 0;
    public isProjectResponsible: boolean = false;
    public readonly: boolean = true;
    public activity: any;
    public place: any;
    public quantity: number = 1;
    public target: number = 0;
    public unModified: any = null;

    constructor(i18n: I18N, notificationHelper: NotificationHelper, router: Router, private readonly productionService: ProductionService) {
        super(notificationHelper, i18n, router);
    }

    public checkDirty(): boolean {
        if (!this.unModified) {
            return false;
        }

        const stringifyUnmodified = JSON.stringify(this.unModified).replace(/[^0-9A-Z]+/gi, "");

        const modified = this.buildProductionDto();
        const stringifyCurrent = JSON.stringify(modified).replace(/[^0-9A-Z]+/gi, "");

        return stringifyUnmodified !== stringifyCurrent;
    }

    public async activate(params: any): Promise<any> {
        this.bindViewModel(params.dispatchProjectCode, params.dailyEntryDate, params.id, params.line, params.q);
        await this.loadData();
    }

    public async loadData(): Promise<any> {
        if (this.productionItemId && this.line) {
            this.initialiseProductionsItem((await this.productionService.GetProductionItem(this.dispatchProjectCode, this.dispatchDate, this.productionItemId, this.line))!);
        }
        this.unModified = this.buildProductionDto();
    }

    public get getActivities(): any {
        return {
            transport: async (params: any, success: any): Promise<any> => {
                await projectService.getActivitiesForDispatchProject(this.dispatchProjectCode, params.data.filter, params.data.page || 1).then((data: any) => {
                    return success(data);
                });
            },
            mapResults: (item: any): any => {
                return { id: item.Id, text: `${item.Id} - ${item.Description}`, productionUnit: item.ProductionUnit };
            }
        };
    }

    public get getPlaces(): any {
        return {
            transport: async (params: any, success: any): Promise<any> => {
                await this.productionService.GetPlaces(this.dispatchProjectCode, this.activity ? this.activity.id : null, params.data.filter, { page: params.data.page || 1, pageSize: 20 }).then((data: any) => {
                    return success(data);
                });
            },
            mapResults: (item: any): any => {
                return {
                    id: item.Id, text: `${item.Id} - ${item.Description}`, data: {
                        Target: item.Target
                    } };
            }
        };
    }

    public resetProductionItemPlace(): void {
        this.updateProductionItemPlace(null);
    }

    public updateProductionItemPlace(item: any): void {
        this.place = item;
        this.target = item !== null ? item.data.Target : 0;
    }

    public bindViewModel(dispatchProjectCode: string, dispatchDate: Date, id: number, line: number, queryString: string): any {
        this.isProjectResponsible = queryStringHelper.parseIsProjectResponsible(queryString);
        this.readonly = queryStringHelper.parseReadonly(queryString);

        this.dispatchDate = dispatchDate;
        this.dispatchProjectCode = dispatchProjectCode;
        this.line = line;
        this.productionItemId = id;
    }

    public buildProductionDto(): any {
        const data: any = {};

        if (this.productionItemId) {
            data.Id = this.productionItemId;
            data.Line = this.line;
        }

        data.ActivityCode = this.activity ? this.activity.id : "";
        data.ActivityDescription = this.activity ? this.activity.text : "";
        data.Quantity = this.quantity;
        data.PlaceId = this.place ? this.place.id : "";
        data.PlaceDescription = this.place ? this.place.text : "";

        return data;
    }

    public initialiseProductionsItem(data: ProductionModel): any {
        this.readonly = false;

        this.activity = { id: data.ActivityCode, text: `${data.ActivityCode} - ${data.ActivityDescription}`, data: { productionUnit: data.Unit } };
        this.place = null;
        this.place = data.PlaceId !== "" ? { id: data.PlaceId, text: `${data.PlaceId} - ${data.PlaceDescription}`, data: { Target: data.Target } } : null;
        this.target = data.Target;
        this.quantity = data.Quantity;
    }

    public isValid(): boolean {
        if (!this.isActivityValid() || !this.isQuantityValid()) {
             return false;
        }

        return true;
    }

    public isActivityValid(): boolean {
        if (!this.activity) {
            this.notificationHelper.showError(this.i18n.tr("err_ActivityRequired"));
            return false;
        }
        return true;
    }

    public isQuantityValid(): boolean {
        if (!this.quantity) {
            this.notificationHelper.showError(this.i18n.tr("err_QuantityRequired"));
            return false;
        }
        if (this.quantity <= 0) {
            this.notificationHelper.showError(this.i18n.tr("err_MinQuantity"));
            return false;
        }
        return true;
    }

    public async save(): Promise<void> {
        if (this.isValid()) {
            this.unModified = null;
            await this.productionService.SaveProduction(this.dispatchProjectCode, this.dispatchDate, this.buildProductionDto());
            routerHelper.navigateBack();
        }
    }
}
